import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import YearMonthPicker from '../../components/YearMonthPicker';
import analytics from '../../lib/analytics';
import { isSameOrEarlierMonth, isFutureMonth } from '../../lib/dateOrderCheck';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { periodPropTypes } from '../../reducers/propTypes';
import {
  currentlyEmployedSelector,
  periodSelector,
  isPartTimeCourseSelector,
} from '../../reducers/selectors';

const changeDate = (event, setter) => {
  setter(event.target.value);
};

const titleText = currentlyEmployed =>
  currentlyEmployed ? 'Tell us about your time there.' : 'When did you work there?';

const labelText = currentlyEmployed =>
  currentlyEmployed ? 'I plan to leave the company in:' : 'I left the company in:';

const EmploymentDatesPage = ({
  employmentDates,
  currentlyEmployed,
  dispatchAddPageData,
  dispatchWizardNav,
  period,
  isPartTimeCourse,
}) => {
  const [startMonth, setStartMonth] = useState(employmentDates.startMonth);
  const [startYear, setStartYear] = useState(employmentDates.startYear);
  const [endMonth, setEndMonth] = useState(employmentDates.endMonth);
  const [endYear, setEndYear] = useState(employmentDates.endYear);
  const { startDate: periodStartTimestamp } = period;
  const minYear = 1980;
  const endDateMinYear = startYear || minYear;

  const isFilledIn = useMemo(() => {
    return Boolean(startMonth && startYear && endMonth && endYear);
  }, [startMonth, startYear, endMonth, endYear]);

  const isStartDateFilledIn = useMemo(() => {
    return Boolean(startMonth && startYear);
  }, [startMonth, startYear]);

  const periodStartDate = useMemo(() => {
    return new Date(periodStartTimestamp);
  }, [periodStartTimestamp]);

  const maxYear = useMemo(() => {
    return periodStartDate.getFullYear();
  }, [periodStartDate]);

  const isAnError = useMemo(() => {
    if (!isFilledIn) {
      return true;
    }
    const parsedStartMonth = parseInt(startMonth, 10);
    const parsedStartYear = parseInt(startYear, 10);
    const parsedEndYear = parseInt(endYear, 10);
    const parsedEndMonth = parseInt(endMonth, 10);
    const nowDate = new Date(Date.now());
    if (
      isFutureMonth(nowDate.getMonth(), nowDate.getFullYear(), parsedStartMonth, parsedStartYear)
    ) {
      return true;
    }

    if (isSameOrEarlierMonth(parsedStartMonth, parsedStartYear, parsedEndMonth, parsedEndYear)) {
      return true;
    }

    if (parsedEndYear > maxYear) {
      return true;
    }

    return parsedEndYear === maxYear && parsedEndMonth > periodStartDate.getMonth();
  }, [startMonth, startYear, endMonth, endYear, periodStartDate, isFilledIn, maxYear]);

  const errorMessage = useMemo(() => {
    if (!isFilledIn) {
      return '';
    }
    const parsedStartMonth = parseInt(startMonth, 10);
    const parsedStartYear = parseInt(startYear, 10);
    const parsedEndYear = parseInt(endYear, 10);
    const parsedEndMonth = parseInt(endMonth, 10);

    if (isSameOrEarlierMonth(parsedStartMonth, parsedStartYear, parsedEndMonth, parsedEndYear)) {
      const errorText = currentlyEmployed ? 'plan to leave the company.' : 'left the company.';
      return `Please ensure the date you joined the company is before the date you ${errorText}`;
    }

    if (parsedEndYear > maxYear) {
      return `Select a date before your class starts in ${maxYear}`;
    }

    if (parsedEndYear === maxYear && parsedEndMonth > periodStartDate.getMonth()) {
      return `Select a date before your class starts in ${maxYear}`;
    }

    return '';
  }, [
    startMonth,
    startYear,
    endMonth,
    endYear,
    currentlyEmployed,
    maxYear,
    periodStartDate,
    isFilledIn,
  ]);

  const helpText = useMemo(() => {
    if (isPartTimeCourse) {
      return "Remember your payments start 3 months after the last payment we make to the school. It's important that you plan to continue working through the course";
    }

    return 'We need to know when you plan to finish working before you start your full-time course.';
  }, [isPartTimeCourse]);

  const isInvalid = !isFilledIn || isAnError;
  const shouldShowError = isFilledIn && isAnError;

  const handleSubmit = event => {
    event.preventDefault();

    analytics.sendEvent('Click - Next', 'employment-dates');

    const payload = payloadFactory.createAddPageDataPayload('employmentDates', {
      startMonth,
      startYear,
      endMonth,
      endYear,
    });

    dispatchAddPageData(payload);

    dispatchWizardNav({ dir: 'fwd' });
  };

  return (
    <PageLayout title={titleText(currentlyEmployed)} helpText={currentlyEmployed ? helpText : null}>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col--10/16-medium">
            <label htmlFor="month-start-select">
              I joined the company in:
              <YearMonthPicker
                hasError={shouldShowError}
                month={startMonth}
                year={startYear}
                minYear={minYear}
                maxYear={maxYear}
                onMonthChange={e => changeDate(e, setStartMonth)}
                onYearChange={e => changeDate(e, setStartYear)}
                yearId="year-start-select"
                monthId="month-start-select"
                allowFutureDates={false}
              />
            </label>
          </div>
        </div>

        <div className="row-spacer">
          <div className="row">
            <div className="col col--10/16-medium">
              <label htmlFor="month-end-select">
                {labelText(currentlyEmployed)}
                <YearMonthPicker
                  errorMessage={errorMessage}
                  month={endMonth}
                  year={endYear}
                  minYear={endDateMinYear}
                  maxYear={maxYear}
                  onMonthChange={e => changeDate(e, setEndMonth)}
                  onYearChange={e => changeDate(e, setEndYear)}
                  yearId="year-end-select"
                  monthId="month-end-select"
                  disabled={!isStartDateFilledIn}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="row-spacer">
          <Button type="submit" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

EmploymentDatesPage.propTypes = {
  employmentDates: PropTypes.shape({
    startMonth: PropTypes.string.isRequired,
    startYear: PropTypes.string.isRequired,
    endMonth: PropTypes.string.isRequired,
    endYear: PropTypes.string.isRequired,
  }).isRequired,
  currentlyEmployed: PropTypes.bool.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  period: periodPropTypes.isRequired,
  isPartTimeCourse: PropTypes.bool.isRequired,
};

const mapStateToProps = reduxStore => ({
  employmentDates: reduxStore.builder.ui.employmentDates,
  currentlyEmployed: currentlyEmployedSelector(reduxStore),
  period: periodSelector(reduxStore),
  isPartTimeCourse: isPartTimeCourseSelector(reduxStore),
});

const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { EmploymentDatesPage, mapStateToProps, mapDispatchToProps };
export default connect(mapStateToProps, mapDispatchToProps)(EmploymentDatesPage);
